import type { BadgeVariant } from '@/components/ui/badge';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import { getSessionValues } from '@/providers/session-provider/get-session-values';
import { DateTime } from 'luxon';
import type {
  IAvailableReport,
  IReportColumnOperation,
  TReadableMap,
  TVariantMap,
} from './types';

export const AVAILABLE_KINDS = [
  'base',
  'invoicing',
  'brf',
  'tenants',
  'objects',
] as const satisfies (keyof TReadableMap)[];

export const AVAILABLE_AGGREGATION_OPERATIONS: {
  v: IReportColumnOperation;
  d: string;
}[] = [
  { v: 'sum', d: 'Summera' },
  { v: 'count', d: 'Antal' },
  { v: 'max', d: 'Max' },
  { v: 'min', d: 'Min' },
  { v: 'mean', d: 'Genomsnitt' },
  { v: 'var', d: 'Varians' },
  { v: 'std', d: 'Standardavvikelse' },
];

export const AGGREGATION_OPERATION_VARIANTS: Record<
  IReportColumnOperation,
  BadgeVariant['variant']
> = {
  sum: 'green',
  count: 'purple',
  max: 'orange',
  min: 'yellow',
  mean: 'cyan',
  std: 'default',
  var: 'blue',
};

export const VARIANT_MAP = {
  base: 'blue',
  invoicing: 'green',
  brf: 'purple',
  tenants: 'cyan',
  objects: 'orange',
  rentincrease: 'destructive',
} as const satisfies TVariantMap;

export const READABLE_MAP = {
  base: 'Bas',
  invoicing: 'Avisering',
  brf: 'BRF',
  tenants: 'Hyresgäster',
  objects: 'Hyresobjekt',
  rentincrease: 'Avgiftsändringar',
} as const satisfies TReadableMap;

export const SHEET_TRANSLATIONS: Record<string, Record<string, string>> = {
  rent_roll: {
    rent_roll: 'Rent roll',
  },
  invoice_payments: {
    invoice_payments: 'Inbetalningar',
    invoice_payments_overpaid: 'Överbetalningar',
    invoice_payments_unmatched: 'Omatchade',
  },
  tenants: {
    tenants: 'Hyresgäster',
  },
  customer_balance: {
    customer_balance: 'Kundsaldo',
    customer_balance_analysis: 'Analys',
  },
  prepaid: {
    prepaid_revenue: 'Förbetalade intäkter',
    prepaid_revenue_analysis: 'Analys',
  },
  periodised_sales: {
    periodised_sales: 'Periodiserad försäljning',
  },
  invoices: {
    non_rental_invoices: 'Fakturor',
    rental_invoices: 'Avier',
  },
  verifications: {
    verifications: 'Verifikat',
  },
  sales: {
    sales: 'Försäljning',
  },
  prognosis: {
    prognosis: 'Prognos',
  },
  vacancies: {
    vacancies: 'Vakanser',
  },
  moving: {
    moving: 'In & utflytts-rapport',
  },
  overpayments: {
    overpayments: 'Överbetalningar',
    overpaid: 'Överbetalningar',
    overpaid_analysis: 'Överbetalningar analys',
  },
  unmatched_payments: {
    unmatched_payments: 'Omatchade betalningar',
    unmatched: 'Omatchade betalningar',
    unmatched_analysis: 'Omatchade betalningar analys',
  },
  brf_voting: {
    brf_voting: 'Röstlängd',
  },
  brf_members: {
    brf_members: 'Medlemmar',
    brf_members_no_apartment: 'Medlemmar utan lägenhet',
    brf_membership_statistics: '  Statistik',
    brf_owners_no_membership: 'Ägare utan medlemskap',
  },
  vat: {
    vat: 'Momsrapport',
  },
  objects: {
    objects: 'Objektslista',
  },
  debit_rows: {
    debit_rows: 'Hyresgästlista',
  },
  invoices_comparison: {
    non_rental_invoices: 'Fakturor',
    rental_invoices: 'Avier',
  },
  vat_keys: {
    space_vatkeys: 'Momsnycklar hyresobjekt',
    realestate_vatkeys: 'Momsnycklar fastigheter',
    company_vatkeys: 'Momsnycklar företag',
  },
};

export const AVAILABLE_REPORTS: IAvailableReport[] = [
  {
    perms: [
      'contracts.apartmentcontract',
      'contracts.industrialpremisescontract',
    ],
    internalId: 'rentrollexpanded',
    externalName: 'rent_roll',
    kind: 'base',
    verboseName: 'Rent roll',

    description:
      'Samtliga aktiva avtal vid ett givet datum. Innehåller information kring hyresgäster, avtal och objekt.',
    config: {
      report_filters: {
        contract_types: [
          'contracts.apartmentcontract',
          'contracts.industrialpremisescontract',
          'contracts.parkingspotcontract',
          'contracts.outdoorsectioncontract',
        ],
      },
      report_type: 'rent_roll',
      sheet_configurations: {
        rent_roll: {
          exclude_columns: [
            'accounting.automaticcontractinvoicingsetting.admin_fee_ui',
            'accounting.automaticcontractinvoicingsetting.admin_fee_postal',
            'accounting.automaticcontractinvoicingsetting.admin_fee_e_inbox',
            'accounting.automaticcontractinvoicingsetting.admin_fee_email',
            'accounting.automaticcontractinvoicingsetting.admin_fee_sms',
            'accounting.automaticcontractinvoicingsetting.name',
            'accounting.automaticcontractinvoicingsetting.source_identifier',
            'objects.realestate.source_identifier',
            'objects.community.source_identifier',
            'objects.segment.source_identifier',
            'objects.realestate.id',
            'objects.basestructure.id',
            'objects.basespace.id',
            'accounts.tenant.source_identifier',
            'contracts.basecontract_main_contract.id',
            'contracts.basecontract_main_contract.custom_dimension_value',
            'organization.company_invoicing.source_identifier',
            'organization.company_invoicing.id',
            'organization.company_landlord.source_identifier',
            'organization.company_landlord.id',
            'accounts.tenant_bi_tenant.source_identifier',
            'accounts.tenant_bi_tenant.sni_number',
            'accounting.autogiropayerconnection.source_identifier',
            'revenue.indexationsettingusage.source_identifier',
            'revenue.indexationsetting.source_identifier',
            'revenue.indexationtable.source_identifier',
            'objects.region.source_identifier',
            'accounting.automaticcontractinvoicingsetting.custom_id',
            'contracts.basecontract_main_contract.counterpart_notification_interval_months',
            'contracts.basecontract_main_contract.owner_notification_interval_months',
            'accounting.autogiropayerconnection.custom_id',
            'revenue.indexationsettingusage.custom_id',
            'revenue.indexationsetting.custom_id',
            'revenue.indexationtable.custom_id',
            'organization.company_invoicing.sni_number',
            'organization.company_invoicing_tags.referenced_by_tags',
            'organization.company_landlord.sni_number',
            'objects.community.main_address',
            'objects.realestate.public_usage',
            'objects.realestate.calculated_land_area',
            'objects.basestructure.source_identifier',
            'objects.basestructure.address',
            'objects.basestructure.availability_start_date',
            'objects.basestructure.availability_end_date',
            'objects.basestructure.built_date',
            'objects.basestructure.demolition_date',
            'objects.basespace.popular_name',
            'objects.basespace.address',
            'accounts.tenant.sni_number',
            'accounts.tenant.realestates',
            'contracts.basecontract.source_identifier',
            'contracts.basecontract.owner_warning_interval_months',
            'contracts.basecontract.counterpart_warning_interval_months',
            'contracts.basecontract_main_contract.source_identifier',
            'contracts.basecontract_main_contract.signed_time',
            'contracts.basecontract_main_contract.start_date',
            'contracts.basecontract_main_contract.initial_end_date',
            'contracts.basecontract_main_contract.renewed_to',
            'contracts.basecontract_main_contract.last_owner_notification_date',
            'contracts.basecontract_main_contract.renew_interval_months',
            'contracts.basecontract_main_contract.cancellation_signed_time',
            'contracts.basecontract_main_contract.cancellation_time',
            'contracts.basecontract_main_contract.lifetime_end_date',
            'contracts.basecontract_main_contract.pending_re_negotiations',
            'contracts.basecontract_main_contract.cancelled_by_counterpart',
            'contracts.basecontract_main_contract.cancellation_reason',
            'contracts.basecontract_main_contract.invoicing_period',
            'contracts.basecontract_main_contract.invoice_month_interval',
            'contracts.basecontract_main_contract.owner_warning_interval_months',
            'contracts.basecontract_main_contract.counterpart_warning_interval_months',
            'contracts.basecontract.id',
          ],
          column_order: [
            'organization.company_invoicing.name',
            'organization.company_invoicing.org_no',
            'organization.company_invoicing.custom_id',
            'organization.company_invoicing.invoice_identifier',
            'organization.company_invoicing.visiting_address',
            'organization.company_invoicing.postal_address',
            'organization.company_landlord.name',
            'organization.company_landlord.org_no',
            'organization.company_landlord.custom_id',
            'organization.company_landlord.invoice_identifier',
            'organization.company_landlord.visiting_address',
            'organization.company_landlord.postal_address',
            'objects.segment.name',
            'objects.segment.custom_id',
            'objects.segment_tags.referenced_by_tags',
            'objects.region.name',
            'objects.region.custom_id',
            'objects.region_tags.referenced_by_tags',
            'objects.community.name',
            'objects.community.custom_id',
            'objects.realestate.name',
            'objects.realestate.custom_id',
            'objects.realestate.popular_name',
            'objects.realestate.main_address',
            'objects.realestate.registered_land_area',
            'objects.realestate.registered_water_area',
            'objects.realestate.ownership_type',
            'objects.realestate.purchased_date',
            'objects.realestate.sold_date',
            'objects.realestate.financial_responsibility_from',
            'objects.realestate.financial_responsibility_to',
            'objects.realestate.non_financial_responsibility_from',
            'objects.realestate.non_financial_responsibility_to',
            'objects.realestate_tags.referenced_by_tags',
            'objects.basestructure.name',
            'objects.basestructure.custom_id',
            'objects.basestructure.popular_name',
            'objects.basestructure.registered_building_area',
            'objects.basestructure.registered_bi_area',
            'objects.basestructure.registered_tempered_area',
            'objects.basestructure.registered_usable_area',
            'objects.basestructure.registered_gross_area',
            'objects.basestructure_tags.referenced_by_tags',
            'objects.basespace.custom_id',
            'objects.basespace.landlord_custom_id',
            'objects.basespace.source_identifier',
            'objects.basespace.category',
            'objects.basespace.usable_area',
            'objects.basespace.bi_area',
            'objects.basespace.total_area',
            'objects.basespace.tempered_area',
            'objects.basespace.apartment_id',
            'objects.basespace.floors',
            'objects.basespace.status',
            'objects.basespace_tags.referenced_by_tags',
            'accounts.tenant.custom_id',
            'accounts.tenant.id',
            'accounts.tenant.communication_name',
            'accounts.tenant.ssn',
            'accounts.tenant.org_no',
            'accounts.tenant.status',
            'accounts.tenant.phone_number',
            'accounts.tenant.email',
            'accounts.tenant.current_registered_address',
            'accounts.tenant.invoice_address',
            'accounts.tenant.invoice_email',
            'accounts.tenant.gln13_number',
            'accounts.tenant.primary_invoice_distribution_methods',
            'accounts.tenant.secondary_invoice_distribution_methods',
            'accounts.tenant.fmi_bank',
            'accounts.tenant_tags.referenced_by_tags',
            'accounts.tenant_bi_tenant.custom_id',
            'accounts.tenant_bi_tenant.id',
            'accounts.tenant_bi_tenant.communication_name',
            'accounts.tenant_bi_tenant.status',
            'accounts.tenant_bi_tenant.ssn',
            'accounts.tenant_bi_tenant.org_no',
            'accounts.tenant_bi_tenant.phone_number',
            'accounts.tenant_bi_tenant.email',
            'contracts.basecontract.custom_id',
            'contracts.basecontract.id_number',
            'contracts.basecontract.status',
            'contracts.basecontract.signed_time',
            'contracts.basecontract.start_date',
            'contracts.basecontract.last_owner_notification_date',
            'contracts.basecontract.renew_interval_months',
            'contracts.basecontract.renewed_to',
            'contracts.basecontract.counterpart_notification_interval_months',
            'contracts.basecontract.owner_notification_interval_months',
            'contracts.basecontract.cancellation_time',
            'contracts.basecontract.cancellation_signed_time',
            'contracts.basecontract.pending_re_negotiations',
            'contracts.basecontract.cancellation_reason',
            'contracts.basecontract.cancelled_by_counterpart',
            'contracts.basecontract.lifetime_end_date',
            'contracts.basecontract.initial_end_date',
            'contracts.basecontract.invoicing_period',
            'contracts.basecontract.invoice_month_interval',
            'contracts.basecontract.invoicing_customer_reference',
            'contracts.basecontract.invoicing_marking',
            'contracts.basecontract.custom_dimension_value',
            'contracts.basecontract.invoicing_default_vat',
            'contracts.basecontract.invoicing_attempt_autogiro',
            'contracts.basecontract_tags.referenced_by_tags',
            'accounting.autogiropayerconnection.can_autogiro',
            'accounting.autogiropayerconnection.payer_number',
            'accounting.autogiropayerconnection.payment_method_account_number',
            'accounting.autogiropayerconnection.payment_method_bank',
            'contracts.basecontract_main_contract.custom_id',
            'contracts.basecontract_main_contract.id_number',
            'contracts.basecontract_main_contract.status',
            'contracts.basecontract_revenue.private_yearly_base_rent_no_index',
            'contracts.basecontract_revenue.private_base_rent_no_index',
            'contracts.basecontract_revenue.private_yearly_base_rent',
            'contracts.basecontract_revenue.private_base_rent',
            'contracts.basecontract_revenue.private_yearly_non_base_rent',
            'contracts.basecontract_revenue.private_non_base_rent',
            'contracts.basecontract_revenue.private_yearly_discount',
            'contracts.basecontract_revenue.private_discount',
            'contracts.basecontract_revenue.private_yearly_index_value',
            'contracts.basecontract_revenue.private_invoicing_period_value',
            'objects.basespace_revenue.private_yearly_base_rent',
            'objects.basespace_revenue.private_base_rent',
            'objects.basespace_revenue.private_yearly_non_base_rent',
            'objects.basespace_revenue.private_non_base_rent',
            'objects.basespace_revenue.private_yearly_discount',
            'objects.basespace_revenue.private_discount',
            'objects.basespace_revenue.private_invoicing_period_value',
            'objects.basespace_revenue.invoicing_period_value',
            'revenue.indexationtable.name',
            'revenue.indexationsetting.base_month',
            'revenue.indexationsetting.debit_around_month',
            'revenue.indexationsetting.firm_quota',
            'revenue.indexationsetting.min_points_change',
            'revenue.indexationsetting.calculate_with_decimals',
            'revenue.indexationsetting.allow_reduction',
            'revenue.indexationsetting.min_quota',
            'revenue.indexationsetting.max_quota',
            'revenue.indexationsetting.rounding_mode',
            'revenue.indexationsettingusage.source_row_category',
            'revenue.indexationsettingusage.base_year',
            'revenue.indexationsettingusage.revenue_base_value_quota',
            'contracts.basecontract_with_space_revenue.yearly_base_rent_no_index',
            'contracts.basecontract_with_space_revenue.base_rent_no_index',
            'contracts.basecontract_with_space_revenue.yearly_base_rent',
            'contracts.basecontract_with_space_revenue.base_rent',
            'contracts.basecontract_with_space_revenue.yearly_base_rent_per_area',
            'contracts.basecontract_with_space_revenue.base_rent_per_area',
            'contracts.basecontract_with_space_revenue.yearly_non_base_rent',
            'contracts.basecontract_with_space_revenue.non_base_rent',
            'contracts.basecontract_with_space_revenue.yearly_non_base_rent_per_area',
            'contracts.basecontract_with_space_revenue.non_base_rent_per_area',
            'contracts.basecontract_with_space_revenue.total_discount_year_1',
            'contracts.basecontract_with_space_revenue.total_discount_year_2',
            'contracts.basecontract_with_space_revenue.total_discount_year_3',
            'contracts.basecontract_with_space_revenue.total_discount_year_4',
            'contracts.basecontract_with_space_revenue.total_discount_year_5_to_10',
            'contracts.basecontract_with_space_revenue.yearly_base_rent_vat',
            'contracts.basecontract_with_space_revenue.yearly_rent',
            'contracts.basecontract_with_space_revenue.total_12_month_revenue',
            'contracts.basecontract_with_space_revenue.total_remaining_guaranteed_revenue',
          ],
          row_order: {
            columns: [
              'organization.company_invoicing.name',
              'objects.basespace.source_identifier',
            ],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounts.tenant'],
    internalId: 'objects',
    externalName: 'objects',
    kind: 'base',
    verboseName: 'Objektslista',
    description: 'Rapporten visar information om objekt.',
    config: {
      report_type: 'objects',
      report_filters: {
        company_ids: null,
        for_date: DateTime.now().toFormat('yyyy-MM-dd'),
        object_types: [
          'objects.apartment',
          'objects.industrialpremises',
          'objects.parkingspot',
          'objects.outdoorsection',
          'objects.brfapartment',
        ],
        organization_id: getSessionValues().organizationId,
        realestate_ids: null,
      },
      sheet_configurations: {
        objects: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.sni_number',
            'objects.region.source_identifier',
            'objects.community.source_identifier',
            'objects.community.main_address',
            'objects.realestate.source_identifier',
            'objects.realestate.id',
            'objects.realestate.popular_name',
            'objects.basestructure.source_identifier',
            'objects.basestructure.id',
            'objects.basestructure.popular_name',
            'objects.basestructure.address',
            'objects.basespace.id',
            'objects.basespace.base_rent_vat_area',
            'objects.basespace.has_base_rent_vat',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'objects.realestate.id',
              'objects.basespace.category',
            ],
            ascending: true,
          },
          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'organization.company.org_no',
            'organization.company.invoice_identifier',
            'organization.company.visiting_address',
            'organization.company.postal_address',
            'objects.region.name',
            'objects.region.custom_id',
            'objects.community.name',
            'objects.community.custom_id',
            'objects.realestate.name',
            'objects.realestate.custom_id',
            'objects.basestructure.name',
            'objects.basestructure.custom_id',
            'objects.basespace.custom_id',
            'objects.basespace.landlord_custom_id',
            'objects.basespace.source_identifier',
            'objects.basespace.status',
            'objects.basespace.apartment_id',
            'objects.basespace.category',
            'objects.basespace.popular_name',
            'objects.basespace.address',
            'objects.basespace.floors',
            'objects.basespace.usable_area',
            'objects.basespace.bi_area',
            'objects.basespace.total_area',
            'objects.basespace.tempered_area',
            'objects.basespace_tags.referenced_by_tags',
            'objects.basespace_revenue.private_yearly_base_rent',
            'objects.basespace_revenue.private_base_rent',
            'objects.basespace_revenue.private_yearly_non_base_rent',
            'objects.basespace_revenue.private_non_base_rent',
            'objects.basespace_revenue.private_yearly_discount',
            'objects.basespace_revenue.private_discount',
            'objects.basespace_revenue.private_invoicing_period_value',
            'objects.basespace_revenue.invoicing_period_value',
            'contracts.basecontract_with_space_revenue.yearly_base_rent_no_index',
            'contracts.basecontract_with_space_revenue.base_rent_no_index',
            'contracts.basecontract_with_space_revenue.yearly_base_rent',
            'contracts.basecontract_with_space_revenue.base_rent',
            'contracts.basecontract_with_space_revenue.yearly_base_rent_per_area',
            'contracts.basecontract_with_space_revenue.base_rent_per_area',
            'contracts.basecontract_with_space_revenue.yearly_non_base_rent',
            'contracts.basecontract_with_space_revenue.non_base_rent',
            'contracts.basecontract_with_space_revenue.yearly_non_base_rent_per_area',
            'contracts.basecontract_with_space_revenue.non_base_rent_per_area',
            'contracts.basecontract_with_space_revenue.total_discount_year_1',
            'contracts.basecontract_with_space_revenue.total_discount_year_2',
            'contracts.basecontract_with_space_revenue.total_discount_year_3',
            'contracts.basecontract_with_space_revenue.total_discount_year_4',
            'contracts.basecontract_with_space_revenue.total_discount_year_5_to_10',
            'contracts.basecontract_with_space_revenue.yearly_base_rent_vat',
            'contracts.basecontract_with_space_revenue.yearly_rent',
            'contracts.basecontract_with_space_revenue.total_12_month_revenue',
            'contracts.basecontract_with_space_revenue.total_remaining_guaranteed_revenue',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.yearly_base_rent_no_index',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.base_rent_no_index',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.yearly_base_rent',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.base_rent',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.yearly_base_rent_per_area',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.base_rent_per_area',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.yearly_non_base_rent',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.non_base_rent',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.yearly_non_base_rent_per_area',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.non_base_rent_per_area',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.total_discount_year_1',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.total_discount_year_2',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.total_discount_year_3',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.total_discount_year_4',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.total_discount_year_5_to_10',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.yearly_base_rent_vat',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.yearly_rent',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.total_12_month_revenue',
            'brf.brfapartmentinvoiceconfiguration_with_space_revenue.total_remaining_guaranteed_revenue',
          ],

          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounting.invoice'],
    internalId: 'baseinvoicepayments',
    externalName: 'invoice_payments',
    kind: 'invoicing',
    verboseName: 'Inbetalningar',

    description: 'Inbetalningar under ett givet intervall',
    config: {
      report_filters: {
        period_start: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
        period_end: DateTime.now().endOf('month').toFormat('yyyy-MM-dd'),
      },
      report_type: 'invoice_payments',
      sheet_configurations: {
        invoice_payments: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.org_no',
            'organization.company.sni_number',
            'accounting.invoice.main_period_end',
            'accounting.invoice.main_period_start',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.due_date',
            'accounting.invoice.invoice_date',
            'accounting.invoice.id',
            'accounting.invoice.source_identifier',
            'accounting.invoicepayment.organization_user',
            'accounting.invoicepayment.tenant',
            'accounting.invoicepayment.id',
            'accounting.invoicepayment.source_identifier',
            'accounting.invoicepayment.additional_information',
            'organization.company.postal_address',
            'organization.company.visiting_address',
            'accounting.invoicepayment.custom_id',
            'accounting.invoice.custom_id',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'organization.company.invoice_identifier',
            'accounting.invoicepayment.status',
            'accounting.invoicepayment.payment_method',
            'accounting.invoicepayment.registration_date',
            'accounting.invoicepayment.payment_date',
            'accounting.invoicepayment.matched_date',
            'accounting.invoicepayment.ocr_number',
            'accounting.invoicepayment.value',
            'accounting.invoicepayment.sender_name',
            'accounting.invoicepayment.distributed_ocr_number',
            'accounting.invoicepayment.distributed_invoice_number',
            'accounting.invoice.invoice_number',
            'accounting.invoice.ocr_number',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'accounting.invoicepayment.payment_date',
            ],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
        invoice_payments_overpaid: {
          exclude_columns: [
            'accounting.invoice.due_daye_days',
            'accounting.invoice.due_date',
            'accounting.invoice.main_period_end',
            'accounting.invoice.main_period_start',
            'accounting.invoice.invoice_date',
            'accounting.invoice.id',
            'accounting.invoice.source_identifier',
            'accounting.invoicepayment.organization_user',
            'accounting.invoicepayment.tenant',
            'accounting.invoicepayment.additional_information',
            'accounting.invoicepayment.id',
            'accounting.invoicepayment.source_identifier',
            'organization.company.sni_number',
            'organization.company.org_no',
            'organization.company.id',
            'organization.company.source_identifier',
            'organization.company.visiting_address',
            'organization.company.postal_address',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.invoice_identifier',
            'accounting.invoicepayment.status',
            'accounting.invoicepayment.payment_method',
            'accounting.invoicepayment.payment_date',
            'accounting.invoicepayment.registration_date',
            'accounting.invoicepayment.matched_date',
            'accounting.invoicepayment.value',
            'accounting.invoicepayment.ocr_number',
            'accounting.invoicepayment.sender_name',
            'accounting.invoicepayment.distributed_ocr_number',
            'accounting.invoicepayment.distributed_invoice_number',
            'accounting.invoice.invoice_number',
            'accounting.invoice.ocr_number',
            'organization.company.custom_id',
            'accounting.invoicepayment.custom_id',
            'accounting.invoice.custom_id',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'accounting.invoicepayment.payment_date',
            ],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
        invoice_payments_unmatched: {
          exclude_columns: [
            'organization.company.id',
            'organization.company.org_no',
            'organization.company.sni_number',
            'accounting.invoice.ocr_number',
            'accounting.invoice.invoice_number',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.due_date',
            'accounting.invoice.invoice_date',
            'accounting.invoice.id',
            'accounting.invoice.source_identifier',
            'accounting.invoice.main_period_end',
            'accounting.invoice.main_period_start',
            'accounting.invoicepayment.organization_user',
            'accounting.invoicepayment.tenant',
            'accounting.invoicepayment.additional_information',
            'accounting.invoicepayment.matched_date',
            'accounting.invoicepayment.source_identifier',
            'accounting.invoicepayment.id',
            'accounting.invoicepayment.distributed_ocr_number',
            'organization.company.visiting_address',
            'organization.company.postal_address',
            'organization.company.source_identifier',
            'accounting.invoicepayment.distributed_invoice_number',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.invoice_identifier',
            'accounting.invoicepayment.status',
            'accounting.invoicepayment.payment_method',
            'accounting.invoicepayment.payment_date',
            'accounting.invoicepayment.registration_date',
            'accounting.invoicepayment.ocr_number',
            'accounting.invoicepayment.value',
            'accounting.invoicepayment.sender_name',
            'organization.company.custom_id',
            'accounting.invoicepayment.custom_id',
            'accounting.invoice.custom_id',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'accounting.invoicepayment.payment_date',
            ],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounts.tenant'],
    internalId: 'basetenants',
    externalName: 'tenants',
    kind: 'tenants',
    verboseName: 'Kontaktlista hyresgäster',

    description: 'Kontaktinformation till hyresgäster',
    config: {
      report_type: 'tenants',
      sheet_configurations: {
        tenants: {
          exclude_columns: [
            'accounts.tenant.source_identifier',
            'accounts.tenant.id',
            'accounts.tenant.sni_number',
            'accounts.tenant.gln13_number',
            'accounts.tenant.primary_invoice_distribution_methods',
            'accounts.tenant.secondary_invoice_distribution_methods',
            'accounts.tenant.fmi_bank',
            'accounts.tenant.realestates',
          ],
          column_order: [
            'accounts.tenant.status',
            'accounts.tenant.communication_name',
            'accounts.tenant.custom_id',
            'accounts.tenant.ssn',
            'accounts.tenant.org_no',
            'accounts.tenant.email',
            'accounts.tenant.phone_number',
            'accounts.tenant.current_registered_address',
            'accounts.tenant.invoice_email',
            'accounts.tenant.invoice_address',
          ],
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    internalId: 'debitrows',
    perms: [
      'contracts.apartmentcontract',
      'contracts.industrialpremisescontract',
      'contracts.parkingspotcontract',
      'contracts.outdoorsectioncontract',
      'contracts.othercontract',
      'contracts.blockcontract',
    ],
    kind: 'tenants',
    externalName: 'debit_rows',
    verboseName: 'Detaljerad hyresgästlista',
    description:
      'Utökad hyresgästlista med information kring avtal, debitering osv.',
    config: {
      report_type: 'debit_rows',
      report_filters: {
        contract_types: [
          'contracts.apartmentcontract',
          'contracts.industrialpremisescontract',
          'contracts.parkingspotcontract',
          'contracts.outdoorsectioncontract',
          'contracts.othercontract',
          'contracts.blockcontract',
        ],
        for_date: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
        include_corporate_tenants: true,
        include_private_tenants: true,
      },
      sheet_configurations: {
        debit_rows: {
          group_by: [],
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.custom_id',
            'organization.company.sni_number',
            'organization.company.visiting_address',
            'organization.company.postal_address',
            'organization.company_invoicing_tags.referenced_by_tags',
            'objects.region.source_identifier',
            'objects.community.source_identifier',
            'objects.community.main_address',
            'objects.segment.source_identifier',
            'objects.realestate.source_identifier',
            'objects.realestate.calculated_land_area',
            'objects.realestate.public_usage',
            'objects.basestructure.source_identifier',
            'objects.basestructure.availability_start_date',
            'objects.basestructure.availability_end_date',
            'objects.basestructure.built_date',
            'objects.basestructure.demolition_date',
            'objects.basespace.source_identifier',
            'accounts.tenant.source_identifier',
            'accounts.tenant.secondary_invoice_distribution_methods',
            'accounts.tenant.realestates',
            'accounts.tenant_bi_tenant.source_identifier',
            'accounts.tenant_bi_tenant.sni_number',
            'accounting.autogiropayerconnection.source_identifier',
            'accounting.autogiropayerconnection.custom_id',
            'contracts.basecontract.source_identifier',
            'contracts.basecontract.counterpart_warning_interval_months',
            'contracts.basecontract.owner_warning_interval_months',
            'contracts.basecontract.custom_dimension_value',
            'contracts.basecontract_tags.referenced_by_tags',
            'accounting.article.source_identifier',
            'accounting.article.custom_id',
            'revenue.indexationsettingusage.source_identifier',
            'revenue.indexationsettingusage.custom_id',
            'revenue.indexationsetting.source_identifier',
            'revenue.indexationsetting.custom_id',
            'revenue.indexationtable.source_identifier',
            'revenue.indexationtable.custom_id',
            'revenue.baserevenuerow.source_identifier',
            'revenue.baserevenuerow.custom_id',
            'revenue.baserevenuerow.id',
            'organization.company.id',
            'objects.realestate.id',
            'objects.basestructure.id',
            'objects.basespace.id',
            'accounts.tenant.id',
            'accounts.tenant_bi_tenant.id',
            'contracts.basecontract.id',
            'objects.basespace.base_rent_vat_area',
            'objects.basespace.has_base_rent_vat',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],

          filters: [],

          aggregation_operations: [],

          column_order: [
            'organization.company.name',
            'organization.company.org_no',
            'objects.region.name',
            'objects.region.custom_id',
            'organization.company.invoice_identifier',
            'objects.region_tags.referenced_by_tags',
            'objects.community.name',
            'objects.community.custom_id',
            'objects.community_tags.referenced_by_tags',
            'objects.segment.name',
            'objects.segment.custom_id',
            'objects.segment_tags.referenced_by_tags',
            'objects.realestate.name',
            'objects.realestate.custom_id',
            'objects.realestate.popular_name',
            'objects.realestate.main_address',
            'objects.realestate.ownership_type',
            'objects.realestate.purchased_date',
            'objects.realestate.sold_date',
            'objects.realestate.financial_responsibility_from',
            'objects.realestate.financial_responsibility_to',
            'objects.realestate.non_financial_responsibility_from',
            'objects.realestate.non_financial_responsibility_to',
            'objects.realestate.registered_land_area',
            'objects.realestate.registered_water_area',
            'objects.realestate_tags.referenced_by_tags',
            'objects.basestructure.name',
            'objects.basestructure.custom_id',
            'objects.basestructure.popular_name',
            'objects.basestructure.address',
            'objects.basestructure.registered_building_area',
            'objects.basestructure.registered_usable_area',
            'objects.basestructure.registered_gross_area',
            'objects.basestructure.registered_bi_area',
            'objects.basestructure.registered_tempered_area',
            'objects.basestructure_tags.referenced_by_tags',
            'objects.basespace.custom_id',
            'objects.basespace.landlord_custom_id',
            'objects.basespace.category',
            'objects.basespace.popular_name',
            'objects.basespace.address',
            'objects.basespace.status',
            'objects.basespace.apartment_id',
            'objects.basespace.floors',
            'objects.basespace.bi_area',
            'objects.basespace.usable_area',
            'objects.basespace.total_area',
            'objects.basespace.tempered_area',
            'objects.basespace_tags.referenced_by_tags',
            'accounts.tenant.communication_name',
            'accounts.tenant.custom_id',
            'accounts.tenant.status',
            'accounts.tenant.sni_number',
            'accounts.tenant.ssn',
            'accounts.tenant.org_no',
            'accounts.tenant.phone_number',
            'accounts.tenant.email',
            'accounts.tenant.current_registered_address',
            'accounts.tenant.invoice_address',
            'accounts.tenant.invoice_email',
            'accounts.tenant.gln13_number',
            'accounts.tenant.primary_invoice_distribution_methods',
            'accounts.tenant.fmi_bank',
            'accounts.tenant_tags.referenced_by_tags',
            'accounts.tenant_bi_tenant.communication_name',
            'accounts.tenant_bi_tenant.custom_id',
            'accounts.tenant_bi_tenant.status',
            'accounts.tenant_bi_tenant.ssn',
            'accounts.tenant_bi_tenant.org_no',
            'accounts.tenant_bi_tenant.phone_number',
            'accounts.tenant_bi_tenant.email',
            'accounting.autogiropayerconnection.can_autogiro',
            'accounting.autogiropayerconnection.payment_method_bank',
            'accounting.autogiropayerconnection.payer_number',
            'accounting.autogiropayerconnection.payment_method_account_number',
            'contracts.basecontract.id_number',
            'contracts.basecontract.custom_id',
            'contracts.basecontract.status',
            'contracts.basecontract.signed_time',
            'contracts.basecontract.start_date',
            'contracts.basecontract.initial_end_date',
            'contracts.basecontract.renew_interval_months',
            'contracts.basecontract.last_owner_notification_date',
            'contracts.basecontract.renewed_to',
            'contracts.basecontract.counterpart_notification_interval_months',
            'contracts.basecontract.owner_notification_interval_months',
            'contracts.basecontract.cancellation_time',
            'contracts.basecontract.cancellation_signed_time',
            'contracts.basecontract.cancellation_reason',
            'contracts.basecontract.pending_re_negotiations',
            'contracts.basecontract.cancelled_by_counterpart',
            'contracts.basecontract.lifetime_end_date',
            'contracts.basecontract.invoicing_period',
            'contracts.basecontract.invoice_month_interval',
            'contracts.basecontract.invoicing_customer_reference',
            'contracts.basecontract.invoicing_marking',
            'contracts.basecontract.invoicing_attempt_autogiro',
            'contracts.basecontract.invoicing_default_vat',
            'revenue.baserevenuerow.title',
            'revenue.baserevenuerow.start_date',
            'revenue.baserevenuerow.end_date',
            'revenue.baserevenuerow.monthly_value',
            'revenue.baserevenuerow.monthly_value_with_vat',
            'revenue.baserevenuerow.yearly_value',
            'revenue.baserevenuerow.yearly_value_with_vat',
            'revenue.baserevenuerow.total_invoice_period_value',
            'revenue.baserevenuerow.total_invoice_period_value_with_vat',
            'revenue.baserevenuerow.vat',
            'objects.basespace_revenue.private_yearly_base_rent',
            'objects.basespace_revenue.private_base_rent',
            'objects.basespace_revenue.private_yearly_non_base_rent',
            'objects.basespace_revenue.private_non_base_rent',
            'objects.basespace_revenue.private_yearly_discount',
            'objects.basespace_revenue.private_discount',
            'objects.basespace_revenue.private_invoicing_period_value',
            'objects.basespace_revenue.invoicing_period_value',
            'contracts.basecontract_with_space_revenue.yearly_base_rent_no_index',
            'contracts.basecontract_with_space_revenue.base_rent_no_index',
            'contracts.basecontract_with_space_revenue.yearly_base_rent',
            'contracts.basecontract_with_space_revenue.base_rent',
            'contracts.basecontract_with_space_revenue.yearly_base_rent_per_area',
            'contracts.basecontract_with_space_revenue.base_rent_per_area',
            'contracts.basecontract_with_space_revenue.yearly_non_base_rent',
            'contracts.basecontract_with_space_revenue.non_base_rent',
            'contracts.basecontract_with_space_revenue.yearly_non_base_rent_per_area',
            'contracts.basecontract_with_space_revenue.non_base_rent_per_area',
            'contracts.basecontract_with_space_revenue.total_discount_year_1',
            'contracts.basecontract_with_space_revenue.total_discount_year_2',
            'contracts.basecontract_with_space_revenue.total_discount_year_3',
            'contracts.basecontract_with_space_revenue.total_discount_year_4',
            'contracts.basecontract_with_space_revenue.total_discount_year_5_to_10',
            'contracts.basecontract_with_space_revenue.yearly_base_rent_vat',
            'contracts.basecontract_with_space_revenue.yearly_rent',
            'contracts.basecontract_with_space_revenue.total_12_month_revenue',
            'contracts.basecontract_with_space_revenue.total_remaining_guaranteed_revenue',
            'accounting.article.category',
            'accounting.article.name',
            'revenue.indexationsettingusage.source_row_category',
            'revenue.indexationsettingusage.revenue_base_value_quota',
            'revenue.indexationsettingusage.base_year',
            'revenue.indexationsetting.base_month',
            'revenue.indexationsetting.debit_around_month',
            'revenue.indexationsetting.firm_quota',
            'revenue.indexationsetting.min_points_change',
            'revenue.indexationsetting.calculate_with_decimals',
            'revenue.indexationsetting.allow_reduction',
            'revenue.indexationsetting.min_quota',
            'revenue.indexationsetting.max_quota',
            'revenue.indexationsetting.rounding_mode',
            'revenue.indexationtable.name',
          ],
        },
      },
    },
  },

  {
    perms: ['accounting.invoice'],
    internalId: 'basecustomerbalance',
    externalName: 'customer_balance',
    kind: 'invoicing',
    verboseName: 'Reskontra',

    description:
      'Reskontra för avier och fakturor per ett specifikt datum. Varje rad visar fordran per avi/faktura. ',
    config: {
      report_filters: {
        for_date: DateTime.now().toFormat('yyyy-MM-dd'),
      },
      report_type: 'customer_balance',
      sheet_configurations: {
        customer_balance: {
          exclude_columns: [
            'organization.company.id',
            'organization.company.source_identifier',
            'organization.company.org_no',
            'organization.company.sni_number',
            'organization.company.invoice_identifier',
            'accounting.invoice.id',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoice.net_vat_25',
            'accounting.invoice.net_vat_12',
            'accounting.invoice.net_vat_6',
            'accounting.invoice.vat_25',
            'accounting.invoice.vat_12',
            'accounting.invoice.vat_6',
            'accounting.invoice.total_vat',
            'accounting.invoice.total_non_vat_base',
            'accounting.invoice.total_vat_base',
            'accounting.invoice.source_identifier',
            'accounting.invoice.custom_id',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          filters: [
            {
              column: 'accounting.invoice.invoice_number',
              operator: 'isnull',
              value: false,
            },
          ],
          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'accounting.invoice.ocr_number',
            'accounting.invoice.invoice_number',
            'accounting.invoice.recipients',
            'accounting.invoice.invoice_date',
            'accounting.invoice.due_date',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.total_invoice_value',
            'accounting.invoice.total_debt_invoice_value',
            'accounting.invoice.total_value',
            'accounting.invoice.paid_amount',
            'accounting.invoice.left_to_pay',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'accounting.invoice.ocr_number',
            ],
            ascending: true,
          },
          group_by: [],
        },
        customer_balance_analysis: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.org_no',
            'organization.company.sni_number',
            'organization.company.invoice_identifier',
            'accounting.invoice.source_identifier',
            'accounting.invoice.id',
            'accounting.invoice.net_vat_25',
            'accounting.invoice.net_vat_12',
            'accounting.invoice.net_vat_6',
            'accounting.invoice.vat_25',
            'accounting.invoice.vat_12',
            'accounting.invoice.vat_6',
            'accounting.invoice.total_vat',
            'accounting.invoice.total_non_vat_base',
            'accounting.invoice.total_vat_base',
            'accounting.invoice.total_invoice_value',
            'accounting.invoice.main_period_end',
            'accounting.invoice.main_period_start',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.due_date',
            'accounting.invoice.invoice_date',
            'accounting.invoice.paid_amount',
            'accounting.invoice_analysis.bookkeept_periodised',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          filters: [
            {
              column: 'accounting.invoice.invoice_number',
              operator: 'isnull',
              value: false,
            },
          ],
          column_order: [
            'organization.company.name',
            'accounting.invoice.invoice_number',
            'accounting.invoice.ocr_number',
            'accounting.invoice.recipients',
            'accounting.invoice.left_to_pay',
            'accounting.invoice_analysis.bookkept_receivable',
            'accounting.invoice_analysis.bookkept_receivable_difference',
            'organization.company.custom_id',
            'accounting.invoice.custom_id',
            'accounting.invoice.total_value',
            'accounting.invoice.total_debt_invoice_value',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'accounting.invoice.ocr_number',
            ],
            ascending: true,
          },
          group_by: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounting.invoicepayment'],
    internalId: 'baseprepaid',
    externalName: 'prepaid',
    kind: 'invoicing',
    verboseName: 'Förutbetalade intäkter',

    description:
      'Avstämning av förutbetalade intäkter, tas ut för ett specifikt datum. ',
    config: {
      report_type: 'prepaid',
      report_filters: {
        for_date: DateTime.now().toFormat('yyyy-MM-dd'),
      },
      sheet_configurations: {
        prepaid_revenue: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.sni_number',
            'organization.company.visiting_address',
            'organization.company.postal_address',
            'accounting.invoice.source_identifier',
            'accounting.invoice.id',
            'accounting.invoice.invoice_date',
            'accounting.invoice.due_daye_days',
            'accounting.invoicepayment.source_identifier',
            'accounting.invoicepayment.id',
            'accounting.invoicepayment.matched_date',
            'accounting.invoicepayment.registration_date',
            'accounting.invoicepayment.ocr_number',
            'accounting.invoicepayment.sender_name',
            'accounting.invoicepayment.additional_information',
            'accounting.invoicepayment.payment_method',
            'accounting.invoicepayment.organization_user',
            'accounting.invoicepayment.status',
            'accounting.invoice.due_date',
            'accounting.invoice.custom_id',
            'accounting.invoicepayment.custom_id',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          filters: [
            {
              column: 'accounting.invoice.invoice_number',
              operator: 'isnull',
              value: false,
            },
          ],
          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'organization.company.org_no',
            'organization.company.invoice_identifier',
            'accounting.invoice.ocr_number',
            'accounting.invoice.invoice_number',
            'accounting.invoicepayment.tenant',
            'accounting.invoicepayment.value',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoicepayment.payment_date',
            'accounting.invoicepayment.distributed_ocr_number',
            'accounting.invoicepayment.distributed_invoice_number',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'accounting.invoicepayment.payment_date',
            ],
            ascending: true,
          },
          group_by: [],

          aggregation_operations: [],
        },
        prepaid_revenue_analysis: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.sni_number',
            'organization.company.visiting_address',
            'organization.company.postal_address',
            'accounting.invoice.source_identifier',
            'accounting.invoice.id',
            'accounting.invoice.invoice_date',
            'accounting.invoice.due_date',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoicepayment.source_identifier',
            'accounting.invoicepayment.id',
            'accounting.invoicepayment.matched_date',
            'accounting.invoicepayment.registration_date',
            'accounting.invoicepayment.ocr_number',
            'accounting.invoicepayment.sender_name',
            'accounting.invoicepayment.additional_information',
            'accounting.invoicepayment.payment_method',
            'accounting.invoicepayment.tenant',
            'accounting.invoicepayment.distributed_ocr_number',
            'accounting.invoicepayment.distributed_invoice_number',
            'accounting.invoicepayment.organization_user',
            'accounting.invoicepayment.status',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          filters: [
            {
              column: 'accounting.invoice.invoice_number',
              operator: 'isnull',
              value: false,
            },
          ],
          column_order: [
            'organization.company.name',
            'organization.company.org_no',
            'organization.company.invoice_identifier',
            'accounting.invoice.invoice_number',
            'accounting.invoice.ocr_number',
            'accounting.invoicepayment.payment_date',
            'accounting.invoicepayment.value',
            'accounting.invoicepayment_analysis.bookkept_receivable',
            'accounting.invoicepayment_analysis.bookkept_receivable_difference',
            'organization.company.custom_id',
            'accounting.invoice.custom_id',
            'accounting.invoicepayment.custom_id',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'accounting.invoicepayment.payment_date',
            ],
            ascending: true,
          },
          group_by: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounting.invoicerow'],
    internalId: 'baseperiodisedsales',
    externalName: 'periodised_sales',
    kind: 'invoicing',
    verboseName: 'Periodiserad försäljning',
    description:
      'Rapporten visar en sammanställning av den debitering som har aviserats eller fakturerats för en viss period. Är även möjligt att inkludera ännu ej attesterade avier för kontroll dessa avier saknar avi/fakturanummer samt OCR. ',
    config: {
      report_type: 'periodised_sales',
      report_filters: {
        for_date: DateTime.now().toFormat('yyyy-MM-dd'),
      },
      sheet_configurations: {
        periodised_sales: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.sni_number',
            'organization.company.visiting_address',
            'organization.company.postal_address',
            'accounting.invoice.source_identifier',
            'accounting.invoice.id',
            'accounting.invoice.invoice_date',
            'accounting.invoicerow.source_identifier',
            'accounting.invoicerow.id',
            'accounting.invoicerow.bookkeeping_period_start',
            'accounting.invoicerow.bookkeeping_period_end',
            'accounting.invoicerow.period_length',
            'accounting.invoicerow.quantity',
            'accounting.invoicerow.unit',
            'accounting.invoicerow.vat',
            'accounting.invoicerow.total_vat_value',
            'accounting.invoicerow.total_value',
            'accounting.invoice_analysis.bookkept_receivable',
            'accounting.invoice_analysis.bookkept_receivable_difference',
            'accounting.invoice_analysis.bookkeept_periodised',
            'accounting.invoice.custom_id',
            'accounting.invoicerow.custom_id',
            'accounting.article.custom_id',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          filters: [
            {
              column: 'accounting.invoice.invoice_number',
              operator: 'isnull',
              value: false,
            },
          ],
          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'organization.company.org_no',
            'organization.company.invoice_identifier',
            'accounting.invoice.contract_id_number',
            'accounting.invoice.recipients',
            'accounting.invoice.ocr_number',
            'accounting.invoice.invoice_number',
            'accounting.invoice.due_date',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoicerow.period_start',
            'accounting.invoicerow.period_end',
            'accounting.invoicerow.title',
            'accounting.article.source_identifier',
            'accounting.article.category',
            'accounting.article.name',
            'accounting.invoicerow.used_article_account',
            'accounting.invoicerow.dimensions',
            'accounting.invoicerow.month_net',
            'accounting.invoicerow.total_net',
            'accounting.invoicerow.last_periodisation_date',
            'accounting.invoicerow.remaining_periods',
            'accounting.invoicerow.revenue_net',
            'accounting.invoicerow.periodised_net',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'accounting.invoice.ocr_number',
            ],
            ascending: true,
          },
          group_by: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounting.invoicerow'],
    internalId: 'basesales',
    externalName: 'sales',
    kind: 'invoicing',

    verboseName: 'Försäljning',
    description:
      'Rapporten visar en sammanställning av den debitering som har aviserats eller fakturerats för en viss period.',
    config: {
      report_type: 'sales',
      report_filters: {
        period_start: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
        period_end: DateTime.now().endOf('month').toFormat('yyyy-MM-dd'),
        rental_invoices_filter: null,
        include_unattested_invoices: false,
        for_date: DateTime.now().endOf('month').toFormat('yyyy-MM-dd'),
      },
      sheet_configurations: {
        sales: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.visiting_address',
            'organization.company.postal_address',
            'accounting.invoice.source_identifier',
            'accounting.invoice.id',
            'accounting.invoicerow.id',
            'accounting.invoicerow.bookkeeping_period_start',
            'accounting.invoicerow.bookkeeping_period_end',
            'accounting.invoicerow.period_length',
            'accounting.invoicerow.last_periodisation_date',
            'accounting.invoicerow.remaining_periods',
            'accounting.invoicerow.periodised_net',
            'accounting.invoicerow.revenue_net',
            'organization.company.sni_number',
            'accounting.invoicerow.source_identifier',
            'accounting.article.source_identifier',
            'accounting.invoice.custom_id',
            'accounting.invoicerow.custom_id',
            'accounting.article.custom_id',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'organization.company.org_no',
            'organization.company.invoice_identifier',
            'accounting.invoice.ocr_number',
            'accounting.invoice.invoice_number',
            'accounting.invoice.contract_id_number',
            'accounting.invoice.recipients',
            'accounting.invoice.invoice_date',
            'accounting.invoice.due_date',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoicerow.period_start',
            'accounting.invoicerow.period_end',
            'accounting.invoicerow.title',
            'accounting.article.category',
            'accounting.article.name',
            'accounting.invoicerow.used_article_account',
            'accounting.invoicerow.dimensions',
            'accounting.invoicerow.unit',
            'accounting.invoicerow.quantity',
            'accounting.invoicerow.month_net',
            'accounting.invoicerow.vat',
            'accounting.invoicerow.total_net',
            'accounting.invoicerow.total_vat_value',
            'accounting.invoicerow.total_value',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'accounting.invoice.ocr_number',
            ],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounting.transaction', 'accounting.verification'],
    internalId: 'baseverifications',
    externalName: 'verifications',
    kind: 'invoicing',
    verboseName: 'Bokföringsjournal',

    description:
      'Rapporten visar varje enskild bokföringstransaktion som har skett under ett givet intervall för ett eller flera konton. Används vanligtvis för att stämma av mot bokföringssystemet. Varje rad visar en specifik kontering.',
    config: {
      report_type: 'verifications',
      report_filters: {
        period_start: null,
        period_end: null,
        period_start_bookkeeping: DateTime.now()
          .startOf('month')
          .toFormat('yyyy-MM-dd'),
        period_end_bookkeeping: DateTime.now()
          .startOf('month')
          .toFormat('yyyy-MM-dd'),
        organization_id: getSessionValues().organizationId,
      },
      sheet_configurations: {
        verifications: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.sni_number',
            'organization.company.invoice_identifier',
            'organization.company.visiting_address',
            'organization.company.postal_address',
            'accounting.invoice.source_identifier',
            'accounting.invoice.id',
            'accounting.invoice.invoice_date',
            'accounting.invoice.due_date',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.invoice_number',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoicepayment.source_identifier',
            'accounting.invoicepayment.id',
            'accounting.invoicepayment.payment_date',
            'accounting.invoicepayment.matched_date',
            'accounting.invoicepayment.registration_date',
            'accounting.invoicepayment.sender_name',
            'accounting.invoicepayment.additional_information',
            'accounting.invoicepayment.value',
            'accounting.invoicepayment.payment_method',
            'accounting.invoicepayment.tenant',
            'accounting.invoicepayment.distributed_invoice_number',
            'accounting.invoicepayment.organization_user',
            'accounting.invoicepayment.status',
            'accounting.transaction.source_identifier',
            'accounting.transaction.id',
            'accounting.verification.source_identifier',
            'accounting.invoicepayment.ocr_number',
            'accounting.invoice.custom_id',
            'accounting.invoicepayment.custom_id',
            'accounting.transaction.custom_id',
            'accounting.verification.custom_id',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],

          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'organization.company.org_no',
            'accounting.verification.bookkeeping_date',
            'accounting.transaction.account',
            'accounting.verification.description',
            'accounting.transaction.debit_value',
            'accounting.transaction.credit_value',
            'accounting.transaction.dimensions',
            'accounting.invoice.ocr_number',
            'accounting.invoicepayment.distributed_ocr_number',
          ],
          row_order: {
            columns: ['organization.company.name'],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounting.invoice'],
    internalId: 'baseinvoices',
    externalName: 'invoices',
    kind: 'invoicing',
    verboseName: 'Avier & Fakturor',

    description:
      'Rapporten visar övergripande information för de avier och fakturor som har faktura- eller avidatum under perioden för rapporten. Varje rad i rapporten visar en faktura eller avi.',
    config: {
      report_type: 'invoices',
      report_filters: {
        organization_id: getSessionValues().organizationId,
        for_date: DateTime.now().endOf('month').toFormat('yyyy-MM-dd'),
        rental_invoices_filter: null,
        period_start: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
        period_end: DateTime.now().endOf('month').toFormat('yyyy-MM-dd'),
      },
      sheet_configurations: {
        non_rental_invoices: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.sni_number',
            'accounting.invoice.source_identifier',
            'accounting.invoice.id',
            'accounting.invoice.left_to_pay',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.org_no',
            'organization.company.invoice_identifier',
            'accounting.invoice.ocr_number',
            'accounting.invoice.invoice_number',
            'accounting.invoice.recipients',
            'accounting.invoice.invoice_date',
            'accounting.invoice.due_date',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoice.total_vat_base',
            'accounting.invoice.total_non_vat_base',
            'accounting.invoice.total_vat',
            'accounting.invoice.total_invoice_value',
            'accounting.invoice.net_vat_25',
            'accounting.invoice.vat_25',
            'accounting.invoice.net_vat_12',
            'accounting.invoice.vat_12',
            'accounting.invoice.net_vat_6',
            'accounting.invoice.vat_6',
            'organization.company.custom_id',
            'accounting.invoice.custom_id',
            'accounting.invoice.paid_amount',
            'accounting.invoice.total_value',
            'accounting.invoice.total_debt_invoice_value',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'accounting.invoice.invoice_date',
              'accounting.invoice.recipients',
            ],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
        rental_invoices: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.sni_number',
            'accounting.invoice.source_identifier',
            'accounting.invoice.id',
            'accounting.invoice.left_to_pay',
            'accounting.invoice.custom_id',
            'accounting.invoice.paid_amount',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'organization.company.org_no',
            'organization.company.invoice_identifier',
            'accounting.invoice.ocr_number',
            'accounting.invoice.invoice_number',
            'accounting.invoice.recipients',
            'accounting.invoice.invoice_date',
            'accounting.invoice.due_date',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoice.total_vat_base',
            'accounting.invoice.total_non_vat_base',
            'accounting.invoice.total_vat',
            'accounting.invoice.total_invoice_value',
            'accounting.invoice.net_vat_25',
            'accounting.invoice.vat_25',
            'accounting.invoice.net_vat_12',
            'accounting.invoice.vat_12',
            'accounting.invoice.net_vat_6',
            'accounting.invoice.vat_6',
            'accounting.invoice.total_value',
            'accounting.invoice.total_debt_invoice_value',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'accounting.invoice.invoice_date',
              'accounting.invoice.recipients',
            ],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounts.tenant'],
    internalId: 'baseprognosis',
    externalName: 'prognosis',
    kind: 'invoicing',
    verboseName: 'Aviseringsprognos',

    description:
      'Rapport över de avier som prognostiseras att ställas ut under en given period. Respektive rad i rapporten visar en debiteringsrad på en avi. Rapporten skall användas för att analysera kommande avisering och är ej lämpad för analys av historiska händelser.',
    config: {
      report_type: 'prognosis',
      report_filters: {
        organization_id: getSessionValues().organizationId,
        company_ids: null,
        realestate_ids: null,
        building_ids: null,
        outdoor_area_ids: null,
        parking_lot_ids: null,
        period_start: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
        period_end: DateTime.now()
          .plus({ month: 1 })
          .startOf('month')
          .toFormat('yyyy-MM-dd'),
        include_corporate_tenants: true,
        include_private_tenants: true,
        ignore_co_invoicing_settings: false,
        ignore_automatic_creation_paused: false,
        extend_to_attested: true,
        attempt_update_of_unattested: true,
        override_and_ignore_zero_value_rows: true,
        invoiceable_types: [
          'contracts.apartmentcontract',
          'contracts.industrialpremisescontract',
          'contracts.parkingspotcontract',
          'contracts.outdoorsectioncontract',
          'contracts.othercontract',
          'contracts.blockcontract',
          'brf.brfapartmentinvoiceconfiguration',
        ],
      },
      sheet_configurations: {
        prognosis: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.sni_number',
            'objects.realestate.source_identifier',
            'objects.realestate.id',
            'objects.realestate.popular_name',
            'contracts.basecontract.id',
            'contracts.basecontract.signed_time',
            'contracts.basecontract.start_date',
            'contracts.basecontract.cancellation_time',
            'contracts.basecontract.last_owner_notification_date',
            'contracts.basecontract.renew_interval_months',
            'contracts.basecontract.counterpart_warning_interval_months',
            'contracts.basecontract.owner_warning_interval_months',
            'contracts.basecontract.renewed_to',
            'contracts.basecontract.cancellation_signed_time',
            'contracts.basecontract.initial_end_date',
            'contracts.basecontract.cancellation_reason',
            'contracts.basecontract.custom_dimension_value',
            'contracts.basecontract.status',
            'contracts.basecontract.lifetime_end_date',
            'contracts.basecontract.pending_re_negotiations',
            'contracts.basecontract.cancelled_by_counterpart',
            'objects.basespace.id',
            'objects.basespace.landlord_custom_id',
            'objects.basespace.popular_name',
            'objects.basespace.usable_area',
            'objects.basespace.bi_area',
            'objects.basespace.total_area',
            'objects.basespace.floors',
            'objects.basespace.tempered_area',
            'objects.basespace.status',
            'accounting.invoicerow.id',
            'accounting.invoicerow.bookkeeping_period_start',
            'accounting.invoicerow.bookkeeping_period_end',
            'accounting.invoicerow.period_length',
            'accounting.invoicerow.last_periodisation_date',
            'accounting.invoicerow.remaining_periods',
            'accounting.invoicerow.periodised_net',
            'accounting.invoicerow.revenue_net',
            'accounting.invoice.id',
            'accounting.invoice.invoice_number',
            'accounting.invoice.ocr_number',
            'accounting.invoicerecipient.source_identifier',
            'accounting.invoicerow.source_identifier',
            'accounting.invoice.source_identifier',
            'accounting.invoicerow.custom_id',
            'accounting.article.custom_id',
            'accounting.invoice.custom_id',
            'accounting.invoicerecipient.custom_id',
            'objects.basespace.base_rent_vat_area',
            'objects.basespace.has_base_rent_vat',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'organization.company.org_no',
            'organization.company.invoice_identifier',
            'objects.realestate.name',
            'objects.realestate.custom_id',
            'objects.basespace.source_identifier',
            'objects.basespace.custom_id',
            'objects.basespace.category',
            'objects.basespace.apartment_id',
            'contracts.basecontract.custom_id',
            'contracts.basecontract.source_identifier',
            'contracts.basecontract.id_number',
            'accounting.invoicerecipient.calculated_name',
            'contracts.basecontract.invoicing_period',
            'accounting.invoice.invoice_date',
            'accounting.invoice.due_date',
            'accounting.invoice.due_daye_days',
            'contracts.basecontract.invoice_month_interval',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoicerow.period_start',
            'accounting.invoicerow.period_end',
            'accounting.invoicerow.title',
            'accounting.article.source_identifier',
            'accounting.article.name',
            'accounting.invoicerow.used_article_account',
            'accounting.invoicerow.dimensions',
            'accounting.invoicerow.quantity',
            'accounting.invoicerow.unit',
            'accounting.invoicerow.vat',
            'accounting.invoicerow.month_net',
            'accounting.invoicerow.total_net',
            'accounting.invoicerow.total_vat_value',
            'accounting.invoicerow.total_value',
            'contracts.basecontract.counterpart_notification_interval_months',
            'contracts.basecontract.owner_notification_interval_months',
          ],
          row_order: {
            columns: [
              'organization.company.name',
              'contracts.basecontract.source_identifier',
              'accounting.invoice.invoice_date',
            ],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounts.tenant'],
    internalId: 'vacancies',
    externalName: 'vacancies',
    kind: 'objects',
    verboseName: 'Vakanser',

    description: 'Rapporten visar information om vakanser för ett givet datum.',
    config: {
      report_filters: {
        for_date: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
        company_ids: null,
        object_types: [
          'objects.apartment',
          'objects.industrialpremises',
          'objects.parkingspot',
          'objects.outdoorsection',
        ],
        realestate_ids: null,
      },
      report_type: 'vacancies',
      sheet_configurations: {
        vacancies: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.sni_number',
            'organization.company.invoice_identifier',
            'organization.company.visiting_address',
            'organization.company.postal_address',
            'objects.basestructure.source_identifier',
            'objects.basestructure.id',
            'objects.basestructure.popular_name',
            'objects.basestructure.address',
            'objects.realestate.source_identifier',
            'objects.realestate.id',
            'objects.realestate.popular_name',
            'objects.basespace.id',
            'objects.basespace.bi_area',
            'objects.basespace.tempered_area',
            'objects.basespace.floors',
            'objects.basespace_revenue.private_yearly_non_base_rent',
            'objects.basespace_revenue.private_non_base_rent',
            'objects.basespace_revenue.private_discount',
            'objects.basespace_revenue.private_yearly_discount',
            'objects.basespace_revenue.private_invoicing_period_value',
            'objects.basespace_revenue.invoicing_period_value',
            'accounting.vacancyinvoice.annulled',
            'accounting.vacancyinvoice.attested_date',
            'accounting.vacancyinvoice.period_start',
            'accounting.vacancyinvoice.custom_id',
            'objects.basespace.base_rent_vat_area',
            'objects.basespace.has_base_rent_vat',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'organization.company.org_no',
            'objects.realestate.name',
            'objects.realestate.custom_id',
            'objects.basestructure.name',
            'objects.basestructure.custom_id',
            'objects.basespace.status',
            'objects.basespace.custom_id',
            'objects.basespace.source_identifier',
            'objects.basespace.category',
            'objects.basespace.landlord_custom_id',
            'objects.basespace.popular_name',
            'objects.basespace.apartment_id',
            'objects.basespace.address',
            'objects.basespace.usable_area',
            'objects.basespace.total_area',
            'objects.basespace_tags.referenced_by_tags',
            'objects.basespace_revenue.private_yearly_base_rent',
            'objects.basespace_revenue.private_base_rent',
            'accounting.vacancyinvoice.source_identifier',
            'objects.basespace_vacancy.vacant_from',
            'objects.basespace_vacancy.vacant_to',
            'accounting.vacancyinvoice.period_end',
          ],
          row_order: {
            columns: ['objects.realestate.name', 'objects.basespace.address'],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounts.tenant'],
    internalId: 'moving',
    externalName: 'moving',
    kind: 'objects',
    verboseName: 'In & utflytts-rapport',

    description:
      'Rapporten visar information om in- och utflyttningar för ett givet datum.',
    config: {
      report_filters: {
        company_ids: null,
        for_date: DateTime.now().endOf('month').toFormat('yyyy-MM-dd'),
        include_corporate_tenants: true,
        include_private_tenants: true,
        object_types: [
          'objects.apartment',
          'objects.industrialpremises',
          'objects.parkingspot',
          'objects.outdoorsection',
        ],
        period_end: DateTime.now().endOf('month').toFormat('yyyy-MM-dd'),
        period_start: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
        realestate_ids: null,
      },
      report_type: 'moving',
      sheet_configurations: {
        moving: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.sni_number',
            'organization.company.invoice_identifier',
            'organization.company.visiting_address',
            'organization.company.postal_address',
            'objects.realestate.source_identifier',
            'objects.realestate.id',
            'objects.realestate.popular_name',
            'objects.basestructure.source_identifier',
            'objects.basestructure.id',
            'objects.basestructure.popular_name',
            'objects.basestructure.address',
            'objects.basespace.id',
            'objects.basespace.bi_area',
            'objects.basespace.tempered_area',
            'objects.basespace.floors',
            'accounts.tenant.realestates',
            'accounts.tenant.secondary_invoice_distribution_methods',
            'accounts.tenant.primary_invoice_distribution_methods',
            'accounts.tenant.gln13_number',
            'accounts.tenant.fmi_bank',
            'accounts.tenant.invoice_address',
            'accounts.tenant.current_registered_address',
            'accounts.tenant.status',
            'accounts.tenant.sni_number',
            'accounts.tenant.org_no',
            'accounts.tenant.ssn',
            'accounts.tenant.id',
            'accounts.tenant.source_identifier',
            'contracts.basecontract.cancellation_signed_time',
            'contracts.basecontract.invoicing_period',
            'contracts.basecontract.renewed_to',
            'contracts.basecontract.counterpart_warning_interval_months',
            'contracts.basecontract.owner_warning_interval_months',
            'contracts.basecontract.renew_interval_months',
            'contracts.basecontract.last_owner_notification_date',
            'contracts.basecontract.cancellation_time',
            'contracts.basecontract.id',
            'contracts.basecontract.source_identifier',
            'contracts.basecontract.signed_time',
            'contracts.basecontract.initial_end_date',
            'contracts.basecontract.cancellation_reason',
            'contracts.basecontract.custom_dimension_value',
            'contracts.basecontract.invoice_month_interval',
            'contracts.basecontract.pending_re_negotiations',
            'contracts.basecontract.cancelled_by_counterpart',
            'contracts.basecontract_revenue.private_discount',
            'contracts.basecontract_revenue.private_yearly_discount',
            'accounts.tenant.invoice_email',
            'objects.basespace_vacancy.vacant_from',
            'objects.basespace_vacancy.vacant_to',
            'objects.basespace_revenue.private_discount',
            'objects.basespace_revenue.private_yearly_discount',
            'contracts.basecontract_revenue.private_yearly_index_value',
            'contracts.basecontract_revenue.private_yearly_base_rent',
            'contracts.basecontract_revenue.private_yearly_non_base_rent',
            'objects.basespace_revenue.private_yearly_base_rent',
            'objects.basespace_revenue.private_yearly_non_base_rent',
            'objects.basespace.base_rent_vat_area',
            'objects.basespace.has_base_rent_vat',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'organization.company.org_no',
            'objects.realestate.custom_id',
            'objects.realestate.name',
            'objects.basestructure.name',
            'objects.basestructure.custom_id',
            'objects.basespace.custom_id',
            'objects.basespace.status',
            'objects.basespace.source_identifier',
            'objects.basespace.category',
            'objects.basespace.landlord_custom_id',
            'objects.basespace.popular_name',
            'objects.basespace.apartment_id',
            'objects.basespace.address',
            'objects.basespace.usable_area',
            'objects.basespace.total_area',
            'objects.basespace_tags.referenced_by_tags',
            'contracts.basecontract.custom_id',
            'contracts.basecontract.id_number',
            'contracts.basecontract.status',
            'contracts.basecontract.start_date',
            'contracts.basecontract.lifetime_end_date',
            'accounts.tenant.custom_id',
            'accounts.tenant.communication_name',
            'accounts.tenant.email',
            'accounts.tenant.phone_number',
            'objects.basespace_revenue.private_base_rent',
            'objects.basespace_revenue.private_non_base_rent',
            'objects.basespace_revenue.private_invoicing_period_value',
            'contracts.basecontract_revenue.private_base_rent',
            'contracts.basecontract_revenue.private_non_base_rent',
            'contracts.basecontract_revenue.private_invoicing_period_value',
            'objects.basespace_revenue.invoicing_period_value',
            'contracts.basecontract.counterpart_notification_interval_months',
            'contracts.basecontract.owner_notification_interval_months',
            'contracts.basecontract_revenue.private_base_rent_no_index',
            'contracts.basecontract_revenue.private_yearly_base_rent_no_index',
          ],
          row_order: {
            columns: ['objects.realestate.name', 'objects.basespace.address'],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['objects.realestate'],
    internalId: 'baserealestates',
    externalName: 'realestate',
    kind: 'base',
    verboseName: 'Fastigheter',

    description: 'Rapporten visar information om fastigheter.',
    config: {
      report_type: 'realestate',
      report_filters: {
        for_date: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
      },
      sheet_configurations: {
        realestate: {
          exclude_columns: [
            'objects.realestate.source_identifier',
            'objects.realestate.id',
            'objects.realestate.calculated_land_area',
            'objects.realestate_calculated.source_identifier',
            'objects.segment.source_identifier',
            'objects.segment.name',
            'objects.segment.custom_id',
            'objects.community.main_address',
            'objects.community.source_identifier',
            'objects.region.source_identifier',
            'objects.realestate_calculated.custom_id',
            'objects.basespace.base_rent_vat_area',
            'objects.basespace.has_base_rent_vat',
          ],
          column_order: [
            'objects.realestate.name',
            'objects.realestate.custom_id',
            'objects.realestate.popular_name',
            'objects.realestate.main_address',
            'objects.region.name',
            'objects.region.custom_id',
            'objects.community.name',
            'objects.community.custom_id',
            'objects.realestate.landlord',
            'objects.realestate.owned_by',
            'objects.realestate.primary_financial_handler',
            'objects.realestate.ownership_type',
            'objects.realestate.purchased_date',
            'objects.realestate.sold_date',
            'objects.realestate.financial_responsibility_from',
            'objects.realestate.financial_responsibility_to',
            'objects.realestate.non_financial_responsibility_from',
            'objects.realestate.non_financial_responsibility_to',
            'objects.realestate.public_usage',
            'objects.realestate.registered_land_area',
            'objects.realestate.registered_water_area',
            'objects.realestate_tags.referenced_by_tags',
            'objects.realestate_calculated.buildings_count',
            'objects.realestate_calculated.contracts_count',
            'objects.realestate_calculated.tenants_count',
            'objects.realestate_calculated.apartments_count',
            'objects.realestate_calculated.brf_apartments_count',
            'objects.realestate_calculated.industrial_premises_count',
            'objects.realestate_calculated.parking_spots_count',
            'objects.realestate_calculated.apartments_usable_area',
            'objects.realestate_calculated.apartments_bi_area',
            'objects.realestate_calculated.brf_apartments_usable_area',
            'objects.realestate_calculated.brf_apartments_bi_area',
            'objects.realestate_calculated.outdoor_sections_usable_area',
            'objects.realestate_calculated.outdoor_sections_bi_area',
            'objects.realestate_calculated.apartments_rented_proportion',
            'objects.realestate_calculated.industrial_premises_usable_area',
            'objects.realestate_calculated.industrial_premises_bi_area',
            'objects.realestate_calculated.industrial_premises_rented_proportion',
            'objects.realestate_calculated.parking_spots_rented_proportion',
            'objects.realestate_calculated.outdoor_sections_count',
            'objects.realestate_calculated.total_rentable_area',
            'objects.realestate_calculated.apartment_and_industrial_premises_rented_proportion',
          ],
          row_order: {
            columns: [
              'objects.realestate.name',
              'objects.realestate.primary_financial_handler',
            ],
            ascending: true,
          },
          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounting.invoicepayment'],
    internalId: 'overpayments',
    externalName: 'overpayments',
    kind: 'invoicing',
    verboseName: 'Överbetalningar',
    featureFlag: FEATURE_FLAGS.PAYMENT_REPORTS,
    description: 'Rapporten visar information om överbetalningar.',
    config: {
      report_type: 'overpayments',
      report_filters: {
        for_date: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
      },
      sheet_configurations: {
        overpaid: {
          exclude_columns: [],
          column_order: [],

          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
        overpaid_analysis: {
          exclude_columns: [],
          column_order: [],
        },
      },
    },
  },
  {
    perms: ['accounting.invoicepayment'],
    internalId: 'unmatched_payments',
    externalName: 'unmatched_payments',
    kind: 'invoicing',
    verboseName: 'Omatchade betalningar',
    featureFlag: FEATURE_FLAGS.PAYMENT_REPORTS,
    description: 'Rapporten visar information om omatchade betalningar.',
    config: {
      report_type: 'unmatched_payments',
      report_filters: {
        for_date: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
      },
      sheet_configurations: {
        unmatched: {
          exclude_columns: [],
          column_order: [],

          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
        unmatched_analysis: {
          exclude_columns: [],
          column_order: [],
        },
      },
    },
  },
  {
    perms: ['brf.brfapartment', 'accounts.tenant'],
    internalId: 'brf_voting',
    externalName: 'brf_voting',
    kind: 'brf',
    verboseName: 'Röstlängd ',
    description: 'Röstlängd för BRF-röster.',
    config: {
      report_type: 'brf_voting',
      report_filters: {
        for_date: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
        brf_company_id: null,
      },
      sheet_configurations: {
        brf_voting: {
          exclude_columns: [
            'accounts.tenant.source_identifier',
            'accounts.tenant.custom_id',
            'accounts.tenant.id',
            'accounts.tenant.org_no',
            'accounts.tenant.ssn',
            'accounts.tenant.phone_number',
            'accounts.tenant.email',
            'accounts.tenant.sni_number',
            'accounts.tenant.status',
            'objects.basespace.source_identifier',
            'objects.basespace.custom_id',
            'objects.basespace.id',
            'objects.basespace.category',
            'objects.basespace.popular_name',
            'objects.basespace.usable_area',
            'objects.basespace.bi_area',
            'objects.basespace.total_area',
            'objects.basespace.floors',
            'objects.basespace.tempered_area',
            'objects.basespace.status',
            'brfvoting.brf_voting_section.transaction_time',
            'objects.basestructure.source_identifier',
            'objects.basestructure.custom_id',
            'objects.basestructure.id',
            'objects.basestructure.name',
            'objects.basestructure.popular_name',
            'objects.realestate.source_identifier',
            'objects.realestate.custom_id',
            'objects.realestate.id',
            'objects.realestate.name',
            'objects.realestate.popular_name',
            'objects.basespace.base_rent_vat_area',
            'objects.basespace.has_base_rent_vat',
          ],
          column_order: [
            'objects.basespace.landlord_custom_id',
            'objects.basespace.address',
            'objects.basespace.apartment_id',
            'accounts.tenant.communication_name',
            'brfvoting.brf_voting_section.owning_quota',
            'brfvoting.brf_voting_section.present_column',
            'brfvoting.brf_voting_section.voting_result_column',
            'brfvoting.brf_voting_section.was_proxy_column',
            'brfvoting.brf_voting_section.signature_column',
            'brfvoting.brf_voting_section.removed_due_to_other_row',
            'brfvoting.brf_voting_section.same_apartment_as_prior_row_index',
          ],

          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['brf.brfapartment', 'accounts.tenant'],
    internalId: 'brf_members',
    externalName: 'brf_members',
    kind: 'brf',
    verboseName: 'BRF-medlemmar',
    featureFlag: FEATURE_FLAGS.BRF_REPORTS,
    description: 'Rapporten visar information om BRF-medlemmar.',
    config: {
      report_type: 'brf_members',
      report_filters: {
        for_date: DateTime.now().startOf('month').toFormat('yyyy-MM-dd'),
        brf_company_id: null,
        include_member_not_owner: true,
        include_owner_not_member: true,
      },
      sheet_configurations: {
        brf_members: {
          exclude_columns: [],
          column_order: [],

          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
        brf_members_no_apartment: {
          exclude_columns: [],
          column_order: [],

          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
        brf_membership_statistics: {
          exclude_columns: [],
          column_order: [],

          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
        brf_owners_no_membership: {
          exclude_columns: [],
          column_order: [],

          group_by: [],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounting.invoice'],
    internalId: 'vat',
    externalName: 'vat',
    kind: 'invoicing',
    verboseName: 'Momsrapport',
    description: 'Rapporten visar information om moms.',
    config: {
      report_type: 'vat',
      report_filters: {
        period_start: DateTime.now()
          .minus({ months: 1 })
          .startOf('month')
          .toFormat('yyyy-MM-dd'),
        period_end: DateTime.now()
          .minus({ months: 1 })
          .endOf('month')
          .toFormat('yyyy-MM-dd'),
      },
      sheet_configurations: {
        vat: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.id',
            'organization.company.sni_number',
            'accounting.invoice.source_identifier',
            'accounting.invoice.id',
            'accounting.invoice.due_date',
            'accounting.invoice.due_daye_days',
            'accounting.invoice_charged.source_identifier',
            'accounting.invoice_toreport.source_identifier',
            'accounting.invoice_resting.source_identifier',
            'accounting.invoice_additional.source_identifier',
            'accounting.invoice_deductible.source_identifier',
            'accounting.invoice.custom_id',
            'accounting.invoice_charged.custom_id',
            'accounting.invoice_deductible.custom_id',
            'accounting.invoice_additional.custom_id',
            'accounting.invoice_toreport.custom_id',
            'accounting.invoice_resting.custom_id',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.custom_id',
            'organization.company.org_no',
            'organization.company.invoice_identifier',
            'accounting.invoice.invoice_date',
            'accounting.invoice.invoice_number',
            'accounting.invoice.ocr_number',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoice_charged.charged_vat_taxable_amount',
            'accounting.invoice_charged.charged_non_vat_taxable_amount',
            'accounting.invoice_charged.charged_vat_amount',
            'accounting.invoice_charged.charged_total_vat_incl_amount',
            'accounting.invoice_deductible.deductible_vat_taxable_amount',
            'accounting.invoice_deductible.deductible_non_vat_taxable_amount',
            'accounting.invoice_deductible.deductible_vat_amount',
            'accounting.invoice_additional.additional_vat_taxable_amount',
            'accounting.invoice_additional.additional_non_vat_taxable_amount',
            'accounting.invoice_additional.additional_vat_amount',
            'accounting.invoice_toreport.to_report_vat_taxable_amount',
            'accounting.invoice_toreport.to_report_non_vat_taxable_amount',
            'accounting.invoice_toreport.to_report_vat_25_amount',
            'accounting.invoice_toreport.to_report_vat_12_amount',
            'accounting.invoice_toreport.to_report_vat_6_amount',
            'accounting.invoice_toreport.to_report_vat_amount',
            'accounting.invoice_resting.resting_vat',
          ],

          row_order: {
            columns: ['organization.company.name'],
            ascending: true,
          },
          group_by: ['organization.company.name'],
          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: ['accounting.invoice'],
    internalId: 'invoices_comparison',
    externalName: 'invoices_comparison',
    kind: 'invoicing',
    verboseName: 'Avier & Fakturor, jämförelse',
    description:
      'Rapporten visar en jämförelse av avier & fakturor från månad till månad, upp till 6 månader bakåt i tiden från vald periodstart',
    config: {
      report_type: 'invoices_comparison',
      report_filters: {
        organization_id: getSessionValues().organizationId,
        period_end: DateTime.now()
          .minus({ months: 1 })
          .endOf('month')
          .toFormat('yyyy-MM-dd'),
        period_start: DateTime.now()
          .minus({ months: 1 })
          .startOf('month')
          .toFormat('yyyy-MM-dd'),
        rental_invoices_filter: null,
        exclude_article_ids: [],
      },
      sheet_configurations: {
        non_rental_invoices: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.custom_id',
            'organization.company.id',
            'organization.company.sni_number',
            'accounting.invoice.source_identifier',
            'accounting.invoice.custom_id',
            'accounting.invoice.id',
            'accounting.invoice.invoice_date',
            'accounting.invoice.due_date',
            'accounting.invoice.recipient_statuses',
            'accounting.invoice.total_invoice_value',
            'accounting.invoice_comparison.source_identifier',
            'accounting.invoice_comparison.custom_id',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.total_vat_base',
            'accounting.invoice.total_non_vat_base',
            'accounting.invoice.total_vat',
            'accounting.invoice.vat_6',
            'accounting.invoice.vat_12',
            'accounting.invoice.vat_25',
            'accounting.invoice.net_vat_6',
            'accounting.invoice.net_vat_12',
            'accounting.invoice.net_vat_25',
            'accounting.invoice_comparison.vat_6',
            'accounting.invoice_comparison.vat_12',
            'accounting.invoice_comparison.vat_25',
            'accounting.invoice_comparison.net_vat_6',
            'accounting.invoice_comparison.net_vat_12',
            'accounting.invoice_comparison.net_vat_25',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.org_no',
            'organization.company.invoice_identifier',
            'accounting.invoice.invoice_number',
            'accounting.invoice.ocr_number',
            'accounting.invoice.recipients',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoice_comparison.total_value',
            'accounting.invoice_comparison.total_vat_base',
            'accounting.invoice_comparison.total_non_vat_base',
            'accounting.invoice_comparison.total_vat',
          ],

          row_order: {
            columns: ['organization.company.name'],
            ascending: true,
          },

          filters: [],
          aggregation_operations: [],
        },
        rental_invoices: {
          exclude_columns: [
            'organization.company.source_identifier',
            'organization.company.custom_id',
            'organization.company.id',
            'organization.company.sni_number',
            'accounting.invoice.source_identifier',
            'accounting.invoice.custom_id',
            'accounting.invoice.id',
            'accounting.invoice.invoice_date',
            'accounting.invoice.due_date',
            'accounting.invoice.recipient_statuses',
            'accounting.invoice.total_invoice_value',
            'accounting.invoice_comparison.source_identifier',
            'accounting.invoice_comparison.custom_id',
            'accounting.invoice.due_daye_days',
            'accounting.invoice.total_vat_base',
            'accounting.invoice.total_non_vat_base',
            'accounting.invoice.total_vat',
            'accounting.invoice.vat_6',
            'accounting.invoice.vat_12',
            'accounting.invoice.vat_25',
            'accounting.invoice.net_vat_6',
            'accounting.invoice.net_vat_12',
            'accounting.invoice.net_vat_25',
            'accounting.invoice_comparison.vat_6',
            'accounting.invoice_comparison.vat_12',
            'accounting.invoice_comparison.vat_25',
            'accounting.invoice_comparison.net_vat_6',
            'accounting.invoice_comparison.net_vat_12',
            'accounting.invoice_comparison.net_vat_25',
            'organization.company.calculated_base_rent_vat_quota',
            'organization.company.calculated_usable_area_rentable_base_rent_vat',
          ],
          column_order: [
            'organization.company.name',
            'organization.company.org_no',
            'organization.company.invoice_identifier',
            'accounting.invoice.invoice_number',
            'accounting.invoice.ocr_number',
            'accounting.invoice.recipients',
            'accounting.invoice.main_period_start',
            'accounting.invoice.main_period_end',
            'accounting.invoice_comparison.total_value',
            'accounting.invoice_comparison.total_vat_base',
            'accounting.invoice_comparison.total_non_vat_base',
            'accounting.invoice_comparison.total_vat',
          ],

          row_order: {
            columns: ['organization.company.name'],
            ascending: true,
          },

          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
  {
    perms: [
      'objects.spacebaserentvatstatusnotation',
      'objects.realestatevatkeyquotanotation',
    ],
    internalId: 'vat_keys',
    externalName: 'vat_keys',
    kind: 'base',
    featureFlag: FEATURE_FLAGS.VAT_KEYS_REPORT,
    verboseName: 'Momsnycklar',
    description: 'Rapporten visar momsnycklar för alla objekt och företag.',
    config: {
      report_type: 'vat_keys',
      report_filters: {
        organization_id: getSessionValues().organizationId,
        for_date: DateTime.now().toFormat('yyyy-MM-dd'),
      },
      sheet_configurations: {
        space_vatkeys: {
          exclude_columns: [],
          column_order: [],

          filters: [],
          aggregation_operations: [],
        },
        realestate_vatkeys: {
          exclude_columns: [],
          column_order: [],

          filters: [],
          aggregation_operations: [],
        },
        company_vatkeys: {
          exclude_columns: [],
          column_order: [],

          filters: [],
          aggregation_operations: [],
        },
      },
    },
  },
];
